import { useSyncExternalStore } from 'react';
import type { Ref } from 'react';
import { TbMenu, TbSearch, TbShoppingCartFilled, TbX } from 'react-icons/tb';
import { panelIds } from './types';
import type { MobileNavigationProps, OpenPanel, Panel } from './types';
import {
  getCartCountSnapshot,
  getServerCartCountSnapshot,
  subscribeCartCount,
} from '../../stores/cart_count';

type HeaderProps = Pick<
  MobileNavigationProps,
  'logo' | 'menu' | 'search' | 'cart'
> & {
  panel: Panel;
  onToggle: (panel: OpenPanel) => void;
  headerRef?: Ref<HTMLDivElement>;
};

export default function Header({
  logo,
  menu,
  search,
  cart,
  panel,
  onToggle,
  headerRef,
}: HeaderProps) {
  const liveCount = useSyncExternalStore(
    subscribeCartCount,
    getCartCountSnapshot,
    getServerCartCountSnapshot,
  );
  const cartCount = liveCount ?? cart?.count ?? 0;
  const menuOpen = panel === 'menu';
  const logoImage = logo && <img src={logo.src} alt={logo.alt} />;

  return (
    <div ref={headerRef} className="bio-mobile-header">
      <div className="bio-mobile-header__logo">
        {logo?.href ? <a href={logo.href}>{logoImage}</a> : logoImage}
      </div>
      <nav className="bio-mobile-header__actions" aria-label="Mobile header">
        {search && (
          <button
            type="button"
            aria-label="Open search"
            aria-expanded={panel === 'search'}
            aria-controls={panelIds.search}
            className="bio-mobile-header__control"
            onClick={() => onToggle('search')}
          >
            <TbSearch />
          </button>
        )}
        {cart && (
          <a
            href={cart.href}
            aria-label="Cart"
            className="bio-mobile-header__control"
          >
            <TbShoppingCartFilled />
            <span
              className="bio-mobile-header__cart-count"
              data-mobile-cart-count=""
              aria-live="polite"
            >
              {cartCount}
            </span>
          </a>
        )}
        {menu && (
          <button
            type="button"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls={panelIds.menu}
            className="bio-mobile-header__control"
            onClick={() => onToggle('menu')}
          >
            {menuOpen ? <TbX /> : <TbMenu />}
          </button>
        )}
      </nav>
    </div>
  );
}
